import { Link } from 'evergreen-ui';
import styled from '@emotion/styled';
import { NavLink, Link as EventLink } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import {
  Pane,
  Text,
  ListItem,
  minorScale,
  majorScale,
  Link as AnchorLink,
  Heading,
  Paragraph,
} from 'evergreen-ui';
import Container from '../container/container';
import Btn from '../btn/btn';
import { authUserVar } from '../../helpers/auth';
import { ReactComponent as AppleStoreIcon } from '../../assets/icons/app-store.svg';
import { ReactComponent as GooglePlaystoreIcon } from '../../assets/icons/google-play.svg';
import { ReactComponent as InstagramIcon } from '../../assets/icons/instagram.svg';
import { ReactComponent as TwitterIcon } from '../../assets/icons/twitter.svg';
import { ReactComponent as FacebookIcon } from '../../assets/icons/facebooks.svg';
import WhytelionLogo from '../../assets/images/whytelion-logo.png';
import CokobarLogo from '../../assets/icons/cokobar-logo.svg';

import { textToBoolean } from '../../helpers/functions';
import clientStorage from '../../libs/client-storage';
import { TS_ACCEPT_COOKIES } from '../../constants';
import { VIEWPORT_BREAKPOINTS } from '../../helpers/enums';
import { useAppContext } from '../../providers/cookie-policy';
import useSubdomain from '../../hooks/use-subdomain';

export const AppLink = styled(AnchorLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  &:focus {
    box-shadow: none;
  }
`;

const StyledList = styled(ListItem)`
  &:hover {
    color: var(--secondary-default-color);
  }

  @media screen and (min-width: ${VIEWPORT_BREAKPOINTS.md}px) {
    &:nth-child(2) {
      order: 6;
    }
  }
`;

const Footer = ({ waitlistEnabled }) => {
  const authUser = useReactiveVar(authUserVar);
  const { subdomain } = useSubdomain();
  const isSubdomainPresent = subdomain?.length > 0;
  const isPathDisabled = (paths) => paths.includes(window.location.pathname);

  const endsWithAny = (path, endings) => {
    return endings.some((ending) => path.endsWith(ending));
  };

  const urlPath = window.location.pathname;
  // Check if the path ends with "/buy" or "/register-interest" using the function
  const isLastSegmentMatch = endsWithAny(urlPath, [
    '/buy',
    '/register-interest',
    '/presale',
  ]);

  const isPaymentPage = window.location.pathname.includes('/payment/');

  const { isCookiesAccepted } = useAppContext();

  const isLaptop = useMediaQuery({
    query: `(min-width: ${VIEWPORT_BREAKPOINTS.md}px)`,
  });

  const hasNotAcceptedCookies = clientStorage.getItem(TS_ACCEPT_COOKIES)
    ? false
    : true;

  const FOOTER_NAV_SECTIONS = [
    {
      heading: 'TicketSir',
      items: [
        {
          shown: true,
          title: 'About Us',
          path: '/about-us',
        },
        {
          shown: true,
          title: 'How It Works',
          path: '/how-it-works',
        },
        {
          shown: true,
          title: 'Your Account',
          path: '/account/profile',
        },
      ],
    },
    {
      heading: 'Using TicketSir',
      items: [
        {
          shown: true,
          title: 'Privacy Policy',
          path: '/privacy-policy',
        },
        {
          shown: true,
          title: 'FAQs',
          path: '/faqs',
        },
        {
          shown: true,
          title: 'Terms and Conditions',
          path: '/term-and-condition',
        },
      ],
    },
    {
      heading: 'Media and Updates',
      items: [
        {
          shown: true,
          title: 'Blog',
          path: 'https://whytelion.com/?blog=y',
          isExternal: true,
        },
        {
          shown: true,
          title: 'Contact Us',
          path: '/contact-us',
        },
        {
          shown: true,
          title: 'Help Center',
          path: '/help-center',
        },
      ],
      customComponent: (
        <Pane
          display="flex"
          alignItems="center"
          paddingY={minorScale(2)}
          columnGap={majorScale(2)}
          width="100%"
        >
          <Link
            href={process.env.REACT_APP_INSTAGRAM_LINK}
            target="_blank"
            rel="noreferrer"
            color="neutral"
            aria-label="follow us on instagram"
          >
            <InstagramIcon cursor="pointer" color="#fff" />
          </Link>
          <Link
            href={process.env.REACT_APP_FACEBOOK_LINK}
            target="_blank"
            rel="noreferrer"
            color="neutral"
            aria-label="follow us on linkedin"
          >
            <FacebookIcon cursor="pointer" color="#fff" />
          </Link>
          <Link
            href={process.env.REACT_APP_TWITTER_LINK}
            target="_blank"
            rel="noreferrer"
            color="neutral"
            aria-label="follow us on twitter"
          >
            <TwitterIcon cursor="pointer" color="#fff" />
          </Link>
        </Pane>
      ),
    },
    {
      heading: isSubdomainPresent ? 'Create an event' : 'Download our apps',
      items: [
        {
          shown: textToBoolean(process.env.REACT_APP_SHOW_HOST_EVENT_LINK),
          title: 'Create your events',
          path: '/host/create-event',
          domainOnly: true,
          unauthenticatedRedirect: '/how-to-create-event',
        },
      ],
      customComponentPosition: 'top', // or 'bottom' (default)
      ...(isSubdomainPresent
        ? {}
        : {
            customComponent: (
              <Pane
                display="flex"
                flexWrap="wrap"
                marginY={majorScale(1)}
                paddingBottom={majorScale(2)}
                gap={'1.5rem'}
              >
                {textToBoolean(
                  process.env.REACT_APP_SHOW_APP_DOWNLOAD_LINKS
                ) && (
                  <>
                    <AppLink
                      href={process.env.REACT_APP_APPLE_STORE_TICKETSIR_LINK}
                      target="_blank"
                      rel="noreferrer"
                      color="neutral"
                      aria-label="download app on apple store"
                    >
                      <AppleStoreIcon cursor="pointer" width={120} />
                    </AppLink>
                    <AppLink
                      href={process.env.REACT_APP_GOOGLE_PLAY_TICKETSIR_LINK}
                      target="_blank"
                      rel="noreferrer"
                      color="neutral"
                      aria-label="download app on play store"
                    >
                      <GooglePlaystoreIcon cursor="pointer" width={120} />
                    </AppLink>
                  </>
                )}
              </Pane>
            ),
          }),
    },
  ];

  return (
    <Pane marginTop={majorScale(2)}>
      <Container
        maxWidth="100%"
        paddingTop={hasNotAcceptedCookies ? majorScale(2) : majorScale(9)}
        fontSize="1rem"
        paddingBottom={
          isLaptop && !isCookiesAccepted
            ? majorScale(12)
            : isLaptop && waitlistEnabled
            ? majorScale(10)
            : isLaptop
            ? majorScale(5)
            : !isCookiesAccepted
            ? majorScale(18)
            : waitlistEnabled
            ? majorScale(14)
            : !isLaptop
            ? majorScale(11)
            : majorScale(1)
        }
        // color="var(--text-default-color)"
        backgroundColor="var(--primary-deep-color)"
      >
        <Pane className="row mb-md-5">
          {FOOTER_NAV_SECTIONS.map((section, sectionIndex) => (
            <Pane
              className="col-md-3 col-sm-12 mb-5 mb-md-0"
              key={sectionIndex}
            >
              <Heading
                color="#ffffff"
                fontWeight="700"
                fontSize="1em"
                marginBottom={majorScale(4)}
              >
                {section.heading}
              </Heading>

              {section.customComponent &&
                section.customComponentPosition === 'top' && (
                  <>{section.customComponent}</>
                )}

              {section.items
                .filter((navItem) => navItem.shown !== false) // Handles conditionals
                .map((navItem, i) => {
                  const key = `${sectionIndex}-${i}`;

                  if (navItem.isExternal) {
                    return (
                      <StyledList
                        key={key}
                        color="#ffffff"
                        cursor="pointer"
                        width="fit-content"
                      >
                        <a
                          href={navItem.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            padding: '8px 0px',
                            color: 'inherit',
                            fontSize: '.9rem',
                            textDecoration: 'none',
                          }}
                        >
                          {navItem.title}
                        </a>
                      </StyledList>
                    );
                  }

                  if (navItem.domainOnly) {
                    return (
                      <StyledList
                        key={key}
                        color="#ffffff"
                        cursor="pointer"
                        width="fit-content"
                      >
                        {authUser?.id && authUser?.hostId ? (
                          <EventLink to={navItem.path}>
                            <Btn
                              fontSize="16px"
                              look="pink-filled"
                              marginRight={40}
                              borderColor={'#C4C4C4'}
                              disableColor={'#C4C4C4'}
                              disabled={
                                isPathDisabled([
                                  '/host/create-event',
                                  '/how-to-create-event-sign-in',
                                  '/register-successfully',
                                ]) ||
                                isLastSegmentMatch ||
                                isPaymentPage
                              }
                            >
                              {navItem.title}
                            </Btn>
                          </EventLink>
                        ) : (
                          <EventLink to={navItem.unauthenticatedRedirect}>
                            <Btn
                              look="pink-filled"
                              fontSize="16px"
                              marginRight={40}
                              borderColor={'#C4C4C4'}
                              disableColor={'#C4C4C4'}
                              disabled={
                                isPathDisabled([
                                  '/how-to-create-event',
                                  '/how-to-create-event-sign-in',
                                  '/register-successfully',
                                ]) ||
                                isLastSegmentMatch ||
                                isPaymentPage
                              }
                            >
                              {navItem.title}
                            </Btn>
                          </EventLink>
                        )}
                      </StyledList>
                    );
                  }

                  return (
                    <StyledList
                      key={key}
                      color="#ffffff"
                      cursor="pointer"
                      width="fit-content"
                    >
                      <NavLink
                        exact
                        to={navItem.path}
                        style={{
                          display: 'inline-block',
                          padding: '8px 0px',
                          color: 'inherit',
                          fontSize: '.9rem',
                        }}
                      >
                        {navItem.title}
                      </NavLink>
                    </StyledList>
                  );
                })}
              {/* ✅ Render custom component if provided */}
              {section.customComponent &&
                section.customComponentPosition !== 'top' && (
                  <>{section.customComponent}</>
                )}
            </Pane>
          ))}
        </Pane>

        <Pane
          borderTop="1px solid #ffffff"
          borderBottom="1px solid #ffffff"
          paddingY={majorScale(2)}
          marginY={majorScale(2)}
        >
          <Pane
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={majorScale(2)}
            flexWrap="wrap"
          >
            <Paragraph
              color="#ffffff"
              width={isLaptop ? 'auto' : '100%'}
              textAlign={isLaptop ? 'left' : 'center'}
              fontWeight="400"
              fontSize="1em"
            >
              Our Network:
            </Paragraph>
            <Link
              href="https://whytelion.com/"
              target="_blank"
              rel="noreferrer"
              color="neutral"
              aria-label="follow us on Whytelion"
              display="block"
            >
              <Pane
                is="img"
                src={WhytelionLogo}
                maxWidth="100%"
                maxHeight="40px"
                verticalAlign="middle"
                alt="Whytelion Logo"
                display="inline-block"
              />
            </Link>
            <Link
              href="https://www.cokobar.com/"
              target="_blank"
              rel="noreferrer"
              color="neutral"
              aria-label="follow us on cokobar"
              display="block"
            >
              <Pane
                is="img"
                src={CokobarLogo}
                maxWidth="100%"
                maxHeight="40px"
                verticalAlign="middle"
                alt="Cokobar Logo"
                display="inline-block"
              />
            </Link>
          </Pane>
        </Pane>
        <Pane
          display="flex"
          justifyContent={isLaptop ? 'flex-start' : 'center'}
        >
          <Text
            is="small"
            color="#ffffff"
            textAlign="center"
            fontSize="1em"
            paddingY={minorScale(5)}
          >
            &copy; {new Date().getFullYear()} Ticketsir is a registered
            trademark of Whytelion Ltd.
          </Text>
        </Pane>
      </Container>
    </Pane>
  );
};

export default Footer;
